import {useTranslation} from '@aboutyou/i18n';
import {styled as linariaStyled} from '@linaria/react';
import React from 'react';
import sc, {css} from 'styled-components';
import {COLORS} from '../../../constants/colors';
import {desktopAndUp, mobileOnly} from '../../../constants/customMedia';
import {FONT_SIZES, FONT_WEIGHTS} from '../../../constants/typography';
import {
  ColorBubble,
  ColorBubbleDescriptor,
} from '../../atoms/ColorBubble/ColorBubble';
import {Tooltip} from '../../atoms/Tooltip/Tooltip';

const MAXIMUM_COLORS = 4;

interface ColorBubblesProps {
  colorBubbles?: ColorBubbleDescriptor[];
  className?: string;
  onColorHover?: (colorProductId: number) => void;
  onColorLeave?: VoidFunction;
  onColorClick?: (colorProductId: number) => void;
  activeProductId?: number;
  soldOutColorProductId?: number;
}

export const ColorBubbles: React.FC<ColorBubblesProps> = ({
  colorBubbles,
  className,
  onColorHover,
  onColorLeave,
  onColorClick,
  activeProductId,
  soldOutColorProductId,
}) => {
  const {t} = useTranslation();
  if (!colorBubbles?.length) {
    return null;
  }

  const displayedColorBubbles = colorBubbles.slice(0, MAXIMUM_COLORS);
  const numRemainingColorBubbles =
    colorBubbles.slice(MAXIMUM_COLORS).length ?? 0;

  return (
    <ColorContainer
      data-testid="ColorContainer"
      className={className}
      onMouseLeave={onColorLeave}
    >
      {displayedColorBubbles?.map((colorBubble, i) => (
        <Tooltip
          key={i}
          label={t('Sold Out')}
          show={colorBubble.productId === soldOutColorProductId}
          showChevron
          padding="small"
        >
          <BubbleWrapper
            onMouseOver={() => {
              if (onColorHover) {
                onColorHover(colorBubble.productId);
              }
            }}
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();

              if (onColorClick) {
                onColorClick(colorBubble.productId);
              }
            }}
            data-active={activeProductId === colorBubble.productId}
            $isLightColor={colorBubble.colors.some(color => color.isLightColor)}
          >
            {onColorHover ? (
              <HoverBubble {...colorBubble} />
            ) : (
              <StyledColorBubble {...colorBubble} />
            )}
          </BubbleWrapper>
        </Tooltip>
      ))}
      {numRemainingColorBubbles > 0 && (
        <ColorRemainder>+{numRemainingColorBubbles}</ColorRemainder>
      )}
    </ColorContainer>
  );
};

const StyledColorBubble = sc(ColorBubble)``;

const HoverBubble = sc(ColorBubble)`
  position: relative;
  cursor: pointer;
  border: none;
  box-sizing: border-box;
  box-shadow: inset 0 0 0 2px transparent, 0 0 0 3px transparent;
  ::before {
    content: '';
    position: absolute;
    top: -1px;
    left: -1px;
    right: -1px;
    bottom: -1px;
    border-radius: 100%;
  }
`;

const BubbleWrapper = sc.li<{$isLightColor?: boolean}>`
  line-height: 0;

  ${({$isLightColor}) =>
    $isLightColor &&
    css`
      ${HoverBubble} {
        box-shadow: 0 0 0 0.5px ${COLORS['Outline.Grey']};
      }
    `}

  ${desktopAndUp(css`
    padding: 3px 0;
  `)}

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }

  &:hover {
    ${HoverBubble} {
      box-shadow: inset 0 0 0 2px ${COLORS.White}, 0 0 0 1px ${
  COLORS['Outline.Grey']
};
      ::before {
        border: 1px solid ${COLORS['Outline.Grey']};
        border-left: 1px solid ${COLORS.Grey1};
        animation: spin 1s linear infinite;
      }
    }
  }
  &[data-active='true'] {
    ${HoverBubble} {
      box-shadow: inset 0 0 0 2px ${COLORS.White}, 0 0 0 1px ${
  COLORS['Outline.Grey']
};
      ::before {
        animation: none;
        border-color: transparent;
      }
    }

    ${({$isLightColor}) =>
      mobileOnly(css`
        ${StyledColorBubble} {
          transform: scale(0.5);
          position: relative;

          ${$isLightColor &&
          css`
            border: none;
          `}

          &::after {
            content: '';
            position: absolute;
            border: 1px solid ${COLORS['Outline.Grey']};
            border-radius: 50%;
            inset: 0;
            transform: scale(2);
          }
        }
      `)}
  }

  ${mobileOnly(css`
    ${StyledColorBubble} {
      height: 8px;
      width: 8px;
    }
  `)}
`;

const ColorContainer = linariaStyled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: row;
  gap: 5px;
  align-items: center;
  transition: opacity 300ms ease-in, transform 300ms ease-in;
  justify-content: center;
`;

const ColorRemainder = sc.li`
  color: ${COLORS.Grey1};
  font-size: ${FONT_SIZES.px10};
  line-height: ${FONT_SIZES.px16};
  ${mobileOnly(css`
    font-weight: ${FONT_WEIGHTS.mediumBold};
  `)}
`;
